import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    Shield, Users, Check, ArrowRight, Zap, Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = {
    executive: {
        name: "Executive",
        price: 9.99,
        icon: Shield,
        accent: "#047857",
        description: "Inbox Shield for busy professionals",
        features: [
            "AI email classification",
            "Pay to Reply (set your price)",
            "Calendar Guard",
            "Thread Exit (Irish Goodbye + Ghost)",
            "Slack channel exit",
            "Public policy page",
            "Sender whitelist",
            "10% platform fee on paid replies",
        ],
    },
    business_starter: {
        name: "Business Starter",
        price: 49,
        icon: Users,
        accent: "#D4AF37",
        description: "Verified leads with consent",
        features: [
            "100 verified leads/month",
            "Embeddable consent widget",
            "3-tier unlock system",
            "Configurable discounts",
            "Industry templates",
            "Lead dashboard + CSV export",
            "Consent vault for consumers",
            "Light/dark widget themes",
        ],
    },
    business_growth: {
        name: "Business Growth",
        price: 149,
        icon: Users,
        accent: "#D4AF37",
        popular: true,
        description: "Scale leads with renewals & AI gate",
        features: [
            "500 verified leads/month",
            "Everything in Starter",
            "Consent renewal campaigns",
            "AI shopping agent gate",
            "Marketing window best practices",
            "Lead quality scoring",
            "Webhook support (CRM push)",
            "Priority support",
        ],
    },
    business_scale: {
        name: "Business Scale",
        price: 349,
        icon: Users,
        accent: "#D4AF37",
        description: "Enterprise-grade consent commerce",
        features: [
            "Unlimited verified leads",
            "Everything in Growth",
            "Custom widget branding",
            "API access",
            "Dedicated account manager",
            "Multi-location support",
            "SLA guarantee",
            "White-label option",
        ],
    },
    bundle: {
        name: "Executive + Starter Bundle",
        price: 14.99,
        icon: Zap,
        accent: "#047857",
        description: "Best of both worlds — save $45/mo",
        features: [
            "Full Executive Inbox Shield",
            "100 verified leads/month",
            "Consent widget embed",
            "All executive features",
            "All Starter business features",
            "One account, dual power",
        ],
    },
};

export default function PricingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null);

    const handleSignIn = () => {
        const redirectUrl = window.location.origin + '/onboarding';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleSubscribe = async (planKey) => {
        if (!user) { handleSignIn(); return; }
        setLoading(planKey);
        try {
            const res = await API.post("/subscription/checkout", {
                origin_url: window.location.origin,
                plan: planKey,
            }, { withCredentials: true });
            if (res.data.url) window.location.href = res.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to start checkout");
        }
        setLoading(null);
    };

    return (
        <div className="min-h-screen-safe bg-[#050505]">
            {/* Header */}
            <header className="border-b border-white/5 py-4 px-6 md:px-12">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-[#047857]" />
                        <span className="font-serif italic text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>The Irish Goodbye</span>
                    </Link>
                    {!user ? (
                        <Button onClick={handleSignIn} variant="outline" size="sm" className="border-white/10">Sign In</Button>
                    ) : (
                        <Button onClick={() => navigate(user.account_type === "executive" ? "/dashboard/executive" : "/dashboard/business")} variant="outline" size="sm" className="border-white/10">Dashboard</Button>
                    )}
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-16">
                {/* Hero */}
                <div className="text-center max-w-2xl mx-auto space-y-4">
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                        Simple <span className="italic gold-shimmer">pricing</span>
                    </h1>
                    <p className="text-[#9CA3AF]">No surprises. No enterprise tier. Pick what fits and start in 2 minutes.</p>
                </div>

                {/* Executive */}
                <section>
                    <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#047857] mb-6">For Executives</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
                        <PlanCard plan={PLANS.executive} planKey="executive" onSubscribe={handleSubscribe} loading={loading} />
                        <PlanCard plan={PLANS.bundle} planKey="bundle" onSubscribe={handleSubscribe} loading={loading} />
                    </div>
                </section>

                {/* Business */}
                <section>
                    <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#D4AF37] mb-6">For Businesses</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <PlanCard plan={PLANS.business_starter} planKey="business_starter" onSubscribe={handleSubscribe} loading={loading} />
                        <PlanCard plan={PLANS.business_growth} planKey="business_growth" onSubscribe={handleSubscribe} loading={loading} />
                        <PlanCard plan={PLANS.business_scale} planKey="business_scale" onSubscribe={handleSubscribe} loading={loading} />
                    </div>
                </section>

                {/* FAQ */}
                <section className="max-w-2xl mx-auto space-y-6">
                    <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Questions</h2>
                    {[
                        { q: "Can I switch plans?", a: "Yes — upgrade or downgrade anytime. Changes take effect immediately." },
                        { q: "What happens to Pay to Reply revenue?", a: "90% goes to you, 10% platform fee. Deposited to your connected bank via Stripe." },
                        { q: "Is my data safe?", a: "We never store email content. Only metadata for classification. Full audit trail in your Consent Vault." },
                        { q: "Do I need Outlook?", a: "For executive features, yes — Outlook is currently supported. Gmail coming soon." },
                        { q: "What counts as a 'lead' on business plans?", a: "Each unique consumer who completes a tier verification through your widget." },
                    ].map((faq, i) => (
                        <div key={i} className="border-b border-white/5 pb-4">
                            <p className="text-sm font-medium mb-1">{faq.q}</p>
                            <p className="text-xs text-[#9CA3AF]">{faq.a}</p>
                        </div>
                    ))}
                </section>
            </main>

            {/* Footer */}
            <footer className="py-8 px-6 border-t border-white/5 text-center">
                <p className="text-xs text-[#6B7280]/60">&copy; 2026 The Irish Goodbye — <Link to="/terms" className="hover:text-[#9CA3AF]">Terms</Link> · <Link to="/privacy" className="hover:text-[#9CA3AF]">Privacy</Link></p>
            </footer>
        </div>
    );
}

function PlanCard({ plan, planKey, onSubscribe, loading }) {
    const Icon = plan.icon;
    const isPopular = plan.popular;
    return (
        <Card className={`bg-[#0a0a0a] overflow-hidden relative ${isPopular ? "border-[#D4AF37]/30" : "border-white/5"}`} data-testid={`plan-${planKey}`}>
            {isPopular && <div className="absolute top-0 left-0 right-0 h-1 gold-shimmer" />}
            <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Icon className="h-5 w-5" style={{ color: plan.accent }} />
                        <span className="text-sm font-medium tracking-widest uppercase text-[#9CA3AF]">{plan.name}</span>
                    </div>
                    {isPopular && <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 text-[10px]">Most Popular</Badge>}
                </div>
                <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${isPopular ? "gold-shimmer" : ""}`} style={{ fontFamily: "'Playfair Display', serif" }}>${plan.price}</span>
                    <span className="text-[#9CA3AF] text-sm">/month</span>
                </div>
                <p className="text-xs text-[#6B7280]">{plan.description}</p>
                <div className="space-y-2 pt-2">
                    {plan.features.map((f, i) => (
                        <div key={i} className="flex items-start gap-2">
                            <Check className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: plan.accent }} />
                            <span className="text-xs text-[#9CA3AF]">{f}</span>
                        </div>
                    ))}
                </div>
                <Button onClick={() => onSubscribe(planKey)} disabled={loading === planKey}
                    className="w-full bg-[#047857] text-white hover:bg-[#036046] shadow-[0_0_20px_rgba(4,120,87,0.5)] font-serif italic"
                    data-testid={`subscribe-${planKey}`}>
                    {loading === planKey ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
            </CardContent>
        </Card>
    );
}
